import { Button, Checkbox, Dialog, Intent, Tooltip } from "@blueprintjs/core";
import {
  ButtonsBloc,
  DataTable,
  FieldSet,
  IDataTableColumn,
  useApiEffect,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";

import {
  CompetitionJudgesRequestApi,
  CompetitionRequestApi,
  JudgeCompetitionRequestGridDto,
  JudgeCompetitionRequestSearch,
  JudgeValidationDto,
} from "../../../../api";
import { EmailButton, ExportButton, SaveButton } from "../../../../components";
import { useApiService, useSaveApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../ERoutes";
import styled from "styled-components";
import { useDialog } from "../../../../contexts";

export interface IJudgeValidationDetailProps {
  editMode: boolean;
  competitionRequestId: number;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

export const InlineButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: flex-end;
  justify-content: space-between;
`;

export const JudgeValidationDetail: React.FunctionComponent<IJudgeValidationDetailProps> = ({
  competitionRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const { showDialogPromise } = useDialog();
  const api = useApiService(CompetitionJudgesRequestApi);
  const competitionApi = useApiService(CompetitionRequestApi);
  const [sObjState, setsobjState] = React.useState<any>();

  const [dirty, setdirty] = React.useState(false);
  const [saveVisible, setsaveVisible] = React.useState(false);

  const [isWorkOrBeauty, isWorkOrBeautyLoading] = useApiEffect(
    () => competitionApi.isWorkOrBeauty(competitionRequestId),
    [competitionRequestId]
  );

  const [judgeValidationList, setJudgeValidationList] = React.useState<Array<JudgeValidationDto>>([]);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    availablePageSizes: [5, 15, 25],
    pageSize: 25,
    sortKeys: { name: "ASC" },
  });

  const searchFunction = React.useCallback(
    async (sObj?: JudgeCompetitionRequestSearch) => {
      setsobjState(sObj);
      const s = JudgeCompetitionRequestSearch.fromJS({
        ...sObj,
        competitionRequestId: competitionRequestId,
      });
      const result = await api.baseSearch(s);
      const list = [];
      result.results.map((e) =>
        list.push(
          new JudgeValidationDto({
            competitionJudgeId: e.competitionJudgeId,
            breedValidated: e.breedValidated,
            subActivityValidated: e.subActivityValidated,
          }).toJSON()
        )
      );
      setJudgeValidationList([...list]);
      return result;
    },
    [api, competitionRequestId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: () => {
          setsaveVisible(false);
          return api.saveJudgeValidation(judgeValidationList);
        },
        onSaveFn: () => {
          search();
          setsaveVisible(false);
          setdirty(false);
        },
      }),
      [api, judgeValidationList, search]
    )
  );

  const save = React.useCallback(
    async (value) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.ConfirmSaveWillSendEmail),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult === "yes") {
        saveItem(value);
        return;
      }
    },
    [saveItem, showDialogPromise, t]
  );

  const columnSubActivityCheckBox = React.useCallback(
    (row: JudgeCompetitionRequestGridDto) => {
      const index = judgeValidationList.findIndex((e) => e.competitionJudgeId === row.competitionJudgeId);
      return row.subActivityValidated !== null || judgeValidationList[index].subActivityValidated !== null ? (
        <Checkbox
          checked={
            judgeValidationList.find((e) => e.competitionJudgeId === row.competitionJudgeId).subActivityValidated
          }
          onChange={() => {
            judgeValidationList[index].subActivityValidated = !judgeValidationList[index].subActivityValidated;
            setJudgeValidationList([...judgeValidationList]);
          }}
          disabled
        />
      ) : (
        <ButtonContainer>
          <Button
            minimal={true}
            intent={Intent.SUCCESS}
            icon={<i className="far fa-check-circle fa-lg" />}
            onClick={() => {
              judgeValidationList[index].subActivityValidated = true;
              setJudgeValidationList([...judgeValidationList]);
              setdirty(true);
            }}
          />
          <Button
            minimal={true}
            intent={Intent.DANGER}
            icon={<i className="far fa-times-circle fa-lg" />}
            onClick={() => {
              judgeValidationList[index].subActivityValidated = false;
              setJudgeValidationList([...judgeValidationList]);
              setdirty(true);
            }}
          />
        </ButtonContainer>
      );
    },
    [judgeValidationList]
  );

  const columnBreedCheckBox = React.useCallback(
    (row: JudgeCompetitionRequestGridDto) => {
      const index = judgeValidationList.findIndex((e) => e.competitionJudgeId === row.competitionJudgeId);
      return row.breedValidated !== null || judgeValidationList[index]?.breedValidated !== null ? (
        <Checkbox
          checked={judgeValidationList.find((e) => e.competitionJudgeId === row.competitionJudgeId)?.breedValidated}
          onChange={() => {
            judgeValidationList[index].breedValidated = !judgeValidationList[index].breedValidated;
            setJudgeValidationList([...judgeValidationList]);
            setdirty(true);
          }}
          disabled
        />
      ) : (
        <ButtonContainer>
          <Button
            onClick={() => {
              judgeValidationList[index].breedValidated = true;
              setJudgeValidationList([...judgeValidationList]);
              setdirty(true);
            }}
            intent={Intent.SUCCESS}
            minimal={true}
            icon={<i className="far fa-check-circle fa-lg" />}
          />
          <Button
            onClick={() => {
              judgeValidationList[index].breedValidated = false;
              setJudgeValidationList([...judgeValidationList]);
              setdirty(true);
            }}
            intent={Intent.DANGER}
            minimal={true}
            icon={<i className="far fa-times-circle fa-lg" />}
          />
        </ButtonContainer>
      );
    },
    [judgeValidationList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: JudgeCompetitionRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/judge/view`}>
            {row.name}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (row: JudgeCompetitionRequestGridDto) => (row.judgeEmail ? <EmailButton email={row.judgeEmail} /> : ""),
      },
      {
        header: () => t(ETLCodes.Country),
        fieldName: "country",
      },
      {
        hidden: !isWorkOrBeauty?.isWork,
        header: () => t(ETLCodes.AuthorizedSubActivity),
        comptuted: true,
        alignment: "center",
        fieldName: "subActivityValidated",
        autoFitContent: true,
        render: columnSubActivityCheckBox,
      },
      {
        hidden: !isWorkOrBeauty?.isBeauty,
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.AuthorizedBreed),
        fieldName: "breedValidated",
        alignment: "center",
        autoFitContent: true,
        render: columnBreedCheckBox,
      },
      {
        header: () => t(ETLCodes.IsBanned),
        fieldName: "isSubActivityBanned",
        autoFitContent: true,
        alignment: "center",
        render: (row: JudgeCompetitionRequestGridDto) =>
          (row.isBannedFromBreed || row.isSubActivityBanned) && (
            <Tooltip content={t(ETLCodes.JudgeIsBanned)}>
              <i className="fas fa-exclamation-triangle" />
            </Tooltip>
          ),
      },
    ],
    [columnBreedCheckBox, columnSubActivityCheckBox, isWorkOrBeauty, t]
  );
  React.useEffect(() => {
    console.log(dirty);
  }, [dirty]);

  const ExportFunction = React.useCallback(
    (sObj: JudgeCompetitionRequestSearch) =>
      api.exportXlsFile(JudgeCompetitionRequestSearch.fromJS({ ...sObj, competitionRequestId: competitionRequestId })),
    [api, competitionRequestId]
  );
  return (
    <FieldSet
      title={t(ETLCodes.JudgesValidation)}
      rightElement={<ExportButton exportFunction={ExportFunction} sObj={sObjState} />}
    >
      <DataTable tableState={tableState} loading={loading} columns={columns} />
      {editMode && (
        <ButtonsBloc style={{ float: "right" }}>
          <SaveButton minimal={false} onClick={save} loading={saving} disabled={!dirty} />
        </ButtonsBloc>
      )}
    </FieldSet>
  );
};
